import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import { buildingUtilityApi } from '../../api/buildingUtilityApi';
import { buildingsApi, Building } from '../../api/buildingsApi';
import { BuildingUtilityBooking, BookingStatus } from '../../types';
import { useCrudPermissions } from '../../hooks/useCrudPermissions';
import { BUILDING_UTILITY_BOOKING_PERMISSIONS } from '../../constants/functionPermissions';
import { formatVietnamDate, formatVietnamDateTime, formatVietnamTime } from '../../utils/vietnamDateTime';
import { formatVndAmount } from '../../utils/vndFormat';
import { getUtilityBookingStatusColor, getUtilityBookingStatusLabel } from '../../utils/bookingStatusLabels';
import {
  isUtilityBookingServiceFeeCollection,
  resolveUtilityBookingServiceFee,
} from '../../utils/utilityBookingCost.util';
import {
  getUtilityCollectVoucherLinkLabel,
  getUtilityRefundVoucherLinkLabel,
} from '../../utils/utilityBookingVoucher.util';
import UtilityBookingDepositModal from '../../components/modals/UtilityBookingDepositModal';
import LedgerVoucherModal from '../../components/modals/LedgerVoucherModal';
import UtilityBookingHistoryModal from '../../components/modals/UtilityBookingHistoryModal';
import toast from 'react-hot-toast';

interface FilterOptions {
  status: string;
  paymentMethod: string;
  startDate: string;
  endDate: string;
  search: string;
  page: number;
  limit: number;
}

export const BuildingUtilityBookings: React.FC = () => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const { canCreate, canEdit } = useCrudPermissions(BUILDING_UTILITY_BOOKING_PERMISSIONS);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');
  const [bookings, setBookings] = useState<BuildingUtilityBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [depositModal, setDepositModal] = useState<{
    booking: BuildingUtilityBooking;
    action: 'collect' | 'refund';
  } | null>(null);
  const [voucherModal, setVoucherModal] = useState<{ entryId: string; title: string } | null>(null);
  const [historyModal, setHistoryModal] = useState<BuildingUtilityBooking | null>(null);

  const [filters, setFilters] = useState<FilterOptions>({
    status: '',
    paymentMethod: '',
    startDate: '',
    endDate: '',
    search: '',
    page: 1,
    limit: 25
  });

  useEffect(() => {
    loadBuildings();
  }, []);

  useEffect(() => {
    if (selectedBuildingId) {
      loadBookings();
    } else {
      setBookings([]);
    }
  }, [selectedBuildingId, filters]);

  const loadBuildings = async () => {
    try {
      const response = await buildingsApi.getBuildings();
      if (response && Array.isArray(response)) {
        setBuildings(response);
        setSelectedBuildingId((prev) => applyDefaultBuildingSelection(response, user, prev));
      }
    } catch (err: any) {
      setError('Error loading buildings');
    }
  };

  const loadBookings = async () => {
    if (!selectedBuildingId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('buildingId', selectedBuildingId);
      params.append('page', filters.page.toString());
      params.append('limit', filters.limit.toString());
      
      if (filters.status) params.append('status', filters.status);
      if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.search) params.append('search', filters.search);
      
      const response = await buildingUtilityApi.getBookings(selectedBuildingId, params.toString());
      
      if (response && Array.isArray(response.data)) {
        setBookings(response.data);
        setTotalCount((response as any).total || response.meta?.total || response.data.length);
      } else {
        console.warn('Expected data with array but got:', response);
        setBookings([]);
        setTotalCount(0);
        setError('Invalid data format received from server');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error loading bookings');
      setBookings([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (id: string) => {
    if (!window.confirm('Xác nhận đăng ký này?')) {
      return;
    }

    try {
      await buildingUtilityApi.updateBooking(id, { status: BookingStatus.CONFIRMED });
      await loadBookings();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error confirming booking');
    }
  };

  const handleCancel = async (id: string) => {
    if (!window.confirm('Hủy đăng ký này?')) {
      return;
    }

    try {
      await buildingUtilityApi.updateBooking(id, { status: BookingStatus.CANCELLED });
      await loadBookings();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error cancelling booking');
    }
  };

  const openDepositModal = (booking: BuildingUtilityBooking, action: 'collect' | 'refund') => {
    setDepositModal({ booking, action });
  };

  const openDepositVoucher = async (
    booking: BuildingUtilityBooking,
    kind: 'collect' | 'refund',
  ) => {
    const title =
      kind === 'collect'
        ? getUtilityCollectVoucherLinkLabel(booking)
        : getUtilityRefundVoucherLinkLabel();
    let entryId =
      kind === 'collect'
        ? booking.depositCollectLedgerEntryId
        : booking.depositRefundLedgerEntryId;

    if (!entryId) {
      try {
        const fresh = await buildingUtilityApi.getBooking(booking.id);
        entryId =
          kind === 'collect'
            ? fresh.depositCollectLedgerEntryId
            : fresh.depositRefundLedgerEntryId;
        if (entryId) {
          setBookings((prev) =>
            prev.map((row) =>
              row.id === booking.id
                ? {
                    ...row,
                    depositCollectLedgerEntryId: fresh.depositCollectLedgerEntryId,
                    depositRefundLedgerEntryId: fresh.depositRefundLedgerEntryId,
                  }
                : row,
            ),
          );
        }
      } catch {
        toast.error('Không thể tải thông tin phiếu');
        return;
      }
    }

    if (!entryId) {
      toast.error('Chưa tìm thấy phiếu. Kiểm tra đã thu/hoàn trên sổ quỹ.');
      return;
    }

    setVoucherModal({ entryId, title });
  };

  const handleStartUsage = async (id: string) => {
    if (!window.confirm('Bắt đầu sử dụng tiện ích cho đăng ký này?')) {
      return;
    }

    try {
      await buildingUtilityApi.startBookingUsage(id);
      await loadBookings();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error starting booking');
    }
  };

  const handleComplete = async (id: string) => {
    if (!window.confirm('Hoàn thành đăng ký này?')) {
      return;
    }

    try {
      await buildingUtilityApi.completeBooking(id);
      await loadBookings();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error completing booking');
    }
  };

  const getStatusBadge = (status: BookingStatus, booking?: BuildingUtilityBooking) => {
    const hasDeposit = (booking?.depositAmount ?? 0) > 0;
    const displayStatus =
      status === BookingStatus.CONFIRMED && hasDeposit && booking?.depositPaid
        ? BookingStatus.DEPOSIT_COLLECTED
        : status;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUtilityBookingStatusColor(displayStatus)}`}>
        {getUtilityBookingStatusLabel(displayStatus, lang === 'en' ? 'en' : 'vi')}
      </span>
    );
  };

  const formatUsageTime = (booking: BuildingUtilityBooking) => {
    if (booking.startTime && booking.endTime) {
      const startDate = formatVietnamDate(booking.startTime);
      const endDate = formatVietnamDate(booking.endTime);
      const startTimeLabel = formatVietnamTime(booking.startTime);
      const endTimeLabel = formatVietnamTime(booking.endTime);

      return (
        <>
          <div>
            {startDate}
            <span className="text-blue-600 ml-1">{startTimeLabel}</span>
          </div>
          <div className="text-gray-500">
            đến {endDate}
            <span className="text-blue-600 ml-1">{endTimeLabel}</span>
          </div>
        </>
      );
    }

    if (booking.bookingDate) {
      return <div>{formatVietnamDateTime(booking.bookingDate)}</div>;
    }

    return <span className="text-gray-400">-</span>;
  };

  const renderDepositCell = (booking: BuildingUtilityBooking) => {
    const depositAmount = booking.depositAmount ?? 0;
    if (depositAmount <= 0) {
      return <span className="text-gray-400">Không có</span>;
    }

    return (
      <div>
        <div>{formatVndAmount(depositAmount)}</div>
        <div className="text-xs text-gray-500">
          {booking.depositPaid ? '✅ Đã thu' : '⏳ Chưa thu'}
          {booking.depositRefunded && ' / 🔄 Đã hoàn'}
        </div>
      </div>
    );
  };

  const canCancelBooking = (booking: BuildingUtilityBooking) =>
    booking.status === BookingStatus.PENDING_CONFIRMATION ||
    (booking.status === BookingStatus.CONFIRMED && !booking.depositPaid);

  const renderActionButtons = (booking: BuildingUtilityBooking) => {
    const hasDeposit = (booking.depositAmount ?? 0) > 0;
    const feeCollectionMode = isUtilityBookingServiceFeeCollection(booking);
    const status =
      booking.status === BookingStatus.CONFIRMED && hasDeposit && booking.depositPaid
        ? BookingStatus.DEPOSIT_COLLECTED
        : booking.status;
    const canStart =
      booking.status !== BookingStatus.PENDING_CONFIRMATION &&
      (status === BookingStatus.DEPOSIT_COLLECTED ||
        (status === BookingStatus.CONFIRMED && !hasDeposit && !feeCollectionMode) ||
        (status === BookingStatus.CONFIRMED && feeCollectionMode && booking.depositPaid) ||
        (status === BookingStatus.CONFIRMED && hasDeposit && booking.depositPaid));
    const needsCounterCollection =
      booking.status === BookingStatus.CONFIRMED &&
      !booking.depositPaid &&
      (hasDeposit || feeCollectionMode);
    const canRefund =
      hasDeposit &&
      booking.depositPaid &&
      !booking.depositRefunded &&
      booking.status === BookingStatus.IN_PROGRESS;
    const canComplete =
      booking.status === BookingStatus.DEPOSIT_REFUNDED ||
      (booking.status === BookingStatus.IN_PROGRESS && !hasDeposit);

    return (
      <div className="flex flex-wrap gap-x-2 gap-y-1">
        {canEdit && (
          <Link
            to={`/building-utility-bookings/${booking.id}/edit`}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            Xem
          </Link>
        )}

        <button
          type="button"
          onClick={() => setHistoryModal(booking)}
          className="text-gray-600 hover:text-gray-800 text-sm"
        >
          Lịch sử
        </button>

        {booking.depositPaid && (
          <button
            type="button"
            onClick={() => openDepositVoucher(booking, 'collect')}
            className="text-cyan-600 hover:text-cyan-800 text-sm"
          >
            {getUtilityCollectVoucherLinkLabel(booking)}
          </button>
        )}

        {booking.depositRefunded && (
          <button
            type="button"
            onClick={() => openDepositVoucher(booking, 'refund')}
            className="text-teal-600 hover:text-teal-800 text-sm"
          >
            {getUtilityRefundVoucherLinkLabel()}
          </button>
        )}

        {canEdit && status === BookingStatus.PENDING_CONFIRMATION && (
          <button
            onClick={() => handleConfirm(booking.id)}
            className="text-green-600 hover:text-green-800 text-sm"
          >
            Xác nhận
          </button>
        )}

        {canEdit && needsCounterCollection && (
          <button
            onClick={() => openDepositModal(booking, 'collect')}
            className="text-yellow-600 hover:text-yellow-800 text-sm"
          >
            {feeCollectionMode ? 'Thu phí' : 'Thu cọc'}
          </button>
        )}

        {canEdit && canStart && (
          <button
            onClick={() => handleStartUsage(booking.id)}
            className="text-purple-600 hover:text-purple-800 text-sm"
          >
            Bắt đầu
          </button>
        )}

        {canEdit && canRefund && (
          <button
            onClick={() => openDepositModal(booking, 'refund')}
            className="text-orange-600 hover:text-orange-800 text-sm"
          >
            Hoàn cọc
          </button>
        )}

        {canEdit && canComplete && (
          <button
            onClick={() => handleComplete(booking.id)}
            className="text-green-600 hover:text-green-800 text-sm"
          >
            Hoàn thành
          </button>
        )}

        {canCancelBooking(booking) && (
          <button
            onClick={() => handleCancel(booking.id)}
            className="text-orange-600 hover:text-orange-800 text-sm"
          >
            Hủy
          </button>
        )}

      </div>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const handleFilterChange = (key: keyof FilterOptions, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : (value as number) // Reset to page 1 when changing filters
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      paymentMethod: '',
      startDate: '',
      endDate: '',
      search: '',
      page: 1,
      limit: 25
    });
  };

  const handleApplyFilters = () => {
    loadBookings();
  };

  const getStatusOptions = () => [
    { value: '', label: 'Tất cả trạng thái' },
    { value: 'PENDING_CONFIRMATION', label: 'Chờ xác nhận' },
    { value: 'CONFIRMED', label: 'Đã xác nhận' },
    { value: 'DEPOSIT_COLLECTED', label: 'Đã thu cọc' },
    { value: 'IN_PROGRESS', label: 'Đang diễn ra' },
    { value: 'DEPOSIT_REFUNDED', label: 'Đã hoàn cọc' },
    { value: 'AWAITING_COMPLETION', label: 'Chờ kết thúc' },
    { value: 'COMPLETED', label: 'Hoàn thành' },
    { value: 'PENDING_CANCELLATION', label: 'Chờ hủy' },
    { value: 'CANCELLED', label: 'Đã hủy' }
  ];

  const getPaymentMethodOptions = () => [
    { value: '', label: 'Tất cả phương thức' },
    { value: 'CASH', label: 'Tiền mặt' },
    { value: 'BANK_TRANSFER', label: 'Chuyển khoản' },
    { value: 'MOMO', label: 'MoMo' },
    { value: 'VNPAY', label: 'VNPay' },
    { value: 'ZALOPAY', label: 'ZaloPay' },
    { value: 'PAYOS', label: 'PayOS' },
    { value: 'VIETQR', label: 'VietQR' },
    { value: 'SHINHAN', label: 'Shinhan Bank' },
  ];

  const getTotalPages = () => Math.ceil(totalCount / filters.limit);

  const renderPagination = () => {
    const totalPages = getTotalPages();
    if (totalPages <= 1) return null;

    const pages = [];
    const currentPage = filters.page;
    
    // Add previous button
    pages.push(
      <button
        key="prev"
        onClick={() => handleFilterChange('page', Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className="px-3 py-2 text-sm border border-gray-300 rounded-l-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Trước
      </button>
    );

    // Add page numbers
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handleFilterChange('page', i)}
          className={`px-3 py-2 text-sm border-t border-b border-gray-300 hover:bg-gray-50 ${
            i === currentPage ? 'bg-blue-50 text-blue-600 border-blue-300' : ''
          }`}
        >
          {i}
        </button>
      );
    }

    // Add next button
    pages.push(
      <button
        key="next"
        onClick={() => handleFilterChange('page', Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        className="px-3 py-2 text-sm border border-gray-300 rounded-r-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Tiếp
      </button>
    );

    return (
      <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t">
        <div className="text-sm text-gray-700">
          Hiển thị {Math.min((currentPage - 1) * filters.limit + 1, totalCount)} - {Math.min(currentPage * filters.limit, totalCount)} trong tổng số {totalCount} kết quả
        </div>
        <div className="flex items-center space-x-1">
          {pages}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto py-8">
      {/* Header */}
      <div className="mb-8">
        <nav className="text-sm text-gray-500 mb-4">
          <span>Tiện ích tòa nhà</span> / <span className="text-gray-900">Danh sách đăng ký</span>
        </nav>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Danh sách đăng ký</h1>
        <p className="text-gray-600">Quản lý các đăng ký sử dụng tiện ích</p>
      </div>

      {/* Building Selection */}
      {shouldShowBuildingSelector(buildings) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Chọn tòa nhà:</label>
            <select
              value={selectedBuildingId}
              onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">-- Chọn tòa nhà --</option>
              {buildings.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {selectedBuildingId && (
        <>
          {/* Actions Bar with Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-wrap gap-4 items-center justify-between mb-4">
              <div className="flex gap-4">
                {canCreate && (
                  <Link
                    to={`/building-utility-bookings/add?buildingId=${selectedBuildingId}`}
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 shadow-md"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Tạo đăng ký mới
                  </Link>
                )}

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                  </svg>
                  Bộ lọc
                </button>
              </div>

              <div className="text-sm text-gray-500">
                Tổng: <span className="font-semibold text-gray-900">{totalCount}</span> đăng ký
              </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="border-t pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {/* Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tìm kiếm</label>
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      placeholder="Tìm theo tên tiện ích, căn hộ..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Trạng thái</label>
                    <select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {getStatusOptions().map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Payment Method Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phương thức thanh toán</label>
                    <select
                      value={filters.paymentMethod}
                      onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {getPaymentMethodOptions().map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Từ ngày</label>
                    <input
                      type="date"
                      value={filters.startDate}
                      onChange={(e) => handleFilterChange('startDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Đến ngày</label>
                    <input
                      type="date"
                      value={filters.endDate}
                      onChange={(e) => handleFilterChange('endDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Items per page */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hiển thị</label>
                    <select
                      value={filters.limit}
                      onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={10}>10 kết quả</option>
                      <option value={25}>25 kết quả</option>
                      <option value={50}>50 kết quả</option>
                      <option value={100}>100 kết quả</option>
                    </select>
                  </div>
                </div>

                {/* Filter Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={handleApplyFilters}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Áp dụng
                  </button>
                  <button
                    onClick={handleClearFilters}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Xóa bộ lọc
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-red-800">{error}</span>
                <button
                  onClick={loadBookings}
                  className="ml-auto px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                >
                  Thử lại
                </button>
              </div>
            </div>
          )}

          {/* Bookings Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="text-lg text-gray-600">Đang tải danh sách đăng ký...</div>
              </div>
            ) : bookings.length === 0 ? (
              <div className="p-8 text-center">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0h6m-6 0l-2 2m8-2l2 2m-2-2v6a2 2 0 01-2 2H10a2 2 0 01-2-2v-6" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {Object.values(filters).some(v => v && v !== 1 && v !== 25) ? 'Không tìm thấy kết quả' : 'Chưa có đăng ký nào'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {Object.values(filters).some(v => v && v !== 1 && v !== 25) 
                    ? 'Thử thay đổi bộ lọc để xem thêm kết quả'
                    : 'Bắt đầu bằng cách tạo đăng ký mới'
                  }
                </p>
                {!Object.values(filters).some(v => v && v !== 1 && v !== 25) && (
                  <Link
                    to={`/building-utility-bookings/add?buildingId=${selectedBuildingId}`}
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200"
                  >
                    Tạo đăng ký mới
                  </Link>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          STT
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Căn hộ
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tiện ích
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Thời gian sử dụng
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Số lượng
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Chi phí
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tiền cọc
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Trạng thái
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ngày tạo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Thao tác
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {bookings.map((booking, index) => (
                        <tr key={booking.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {(filters.page - 1) * filters.limit + index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{booking.apartment?.code}</div>
                            <div className="text-sm text-gray-500">
                              {booking.apartmentMember?.fullName || booking.apartment?.ownerName || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="text-lg mr-2">{booking.group?.icon || '🏢'}</span>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{booking.utility?.name}</div>
                                <div className="text-sm text-gray-500">{booking.group?.name}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatUsageTime(booking)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {booking.utility?.group?.utilityType === 'NO_BOOKING' 
                              ? `${booking.numberOfSlots || 1} người`
                              : booking.utility?.group?.utilityType === 'TIME_BASED'
                                ? `${booking.numberOfSlots || 1} giờ`
                                : `${booking.numberOfSlots || 1} slot`
                            }
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {(() => {
                              const serviceFee = resolveUtilityBookingServiceFee(booking);
                              return serviceFee > 0 ? formatCurrency(serviceFee) : '-';
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {renderDepositCell(booking)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(booking.status, booking)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatVietnamDateTime(booking.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {renderActionButtons(booking)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {renderPagination()}
              </>
            )}
          </div>
        </>
      )}
      {depositModal && (
        <UtilityBookingDepositModal
          booking={depositModal.booking}
          action={depositModal.action}
          onClose={() => setDepositModal(null)}
          onCompleted={() => loadBookings()}
        />
      )}
      {voucherModal && (
        <LedgerVoucherModal
          entryId={voucherModal.entryId}
          title={voucherModal.title}
          onClose={() => setVoucherModal(null)}
        />
      )}
      {historyModal && (
        <UtilityBookingHistoryModal
          booking={historyModal}
          onClose={() => setHistoryModal(null)}
        />
      )}
    </div>
  );
}; 